import React, { useState, useEffect } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import {
  AppShell,
  Group,
  Text,
  Collapse,
  NavLink,
  Anchor,
  Image,
  UnstyledButton,
  ScrollArea,
  Burger,
  Stack,
  ActionIcon,
  useMantineColorScheme,
  useComputedColorScheme
} from '@mantine/core';
import {
  IconUsers,
  IconUser,
  IconChevronUp,
  IconPackage,
  IconChevronDown,
  IconAbc,
  IconPlane,
  IconPlaneTilt,
  IconTruck,
  IconFilter,
  IconMail,
  IconScale,
  IconTools,
  IconCurrencyDollar,
  IconBug,
  IconSchool,
  IconVideo,
  IconSearch,
  IconManualGearbox,
  IconChartLine,
  IconFlask,
  IconShieldCheck,
  IconServer,
  IconAtom,
  IconInfoCircle,
  IconBook,
  IconFlag,
  IconSun,
  IconMoon,
  IconPentagon,
  IconReportMoney,
  IconAdjustments,
  IconTag,
  IconChartBar,
  IconTargetArrow
} from '@tabler/icons-react';
import { useMsal } from "@azure/msal-react";
import useAuthenticatedData from '../hooks/useAuthenticatedData';
import config from '../config';
import { buildInitiativeTree } from './initiatives/kpiCatalog';

const DRAWER_WIDTH = 240;

// ---------------------------------------------------------------------------------------------
// Initiatives nav tree — Objective > Pillar > Initiative > KPI.
//
// These live at module scope on purpose. AdminMenu re-renders on every route change (useLocation),
// and a component defined inside it would be a new component type each render, so React would
// unmount and remount the tree and every group would snap shut the moment you clicked a KPI.
// ---------------------------------------------------------------------------------------------

// The tree is derived from static catalog data, so it is built once rather than per render.
const initiativeTree = buildInitiativeTree();

/** Indent per nesting level. Four levels have to fit inside a 240px drawer, so this is tight. */
const TREE_INDENT = (depth) => 12 + depth * 11;

function InitiativeTreeGroup({ label, depth, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <UnstyledButton
        onClick={() => setOpen(!open)}
        w="100%"
        py={5}
        pr={8}
        pl={TREE_INDENT(depth)}
      >
        <Group justify="space-between" gap={6} wrap="nowrap" align="flex-start">
          <Text
            size="xs"
            fw={depth === 0 ? 700 : 500}
            c={depth === 0 ? undefined : 'dimmed'}
            tt={depth === 0 ? 'uppercase' : undefined}
            style={{ lineHeight: 1.3 }}
          >
            {label}
          </Text>
          {open ? (
            <IconChevronUp size={12} style={{ flexShrink: 0, marginTop: 2 }} />
          ) : (
            <IconChevronDown size={12} style={{ flexShrink: 0, marginTop: 2 }} />
          )}
        </Group>
      </UnstyledButton>
      <Collapse in={open}>{children}</Collapse>
    </div>
  );
}

/** A leaf link in the initiatives tree. Used for both KPI pages and the scorecard. */
function InitiativeLink({ href, label, depth, pathname, emphasis = false }) {
  const isActive = pathname === href;
  return (
    <NavLink
      component={RouterLink}
      to={href}
      label={label}
      active={isActive}
      styles={(theme) => ({
        root: {
          paddingLeft: TREE_INDENT(depth),
          paddingTop: 4,
          paddingBottom: 4,
        },
        label: {
          fontSize: theme.fontSizes.xs,
          fontWeight: isActive ? 600 : emphasis ? 600 : 400,
        },
      })}
    />
  );
}

function InitiativeKpiLink({ kpi, depth, pathname }) {
  return (
    <InitiativeLink
      href={`/initiatives/kpi/${kpi.slug}`}
      label={kpi.shortName || kpi.name}
      depth={depth}
      pathname={pathname}
    />
  );
}

// Pillar > Objective > Initiative > KPI, matching docs/initiatives/design.md. The pillar contains
// the objectives, not the reverse — an objective-first tree would render the single pillar twice.
function InitiativeTree({ tree, pathname }) {
  return (
    <>
      {tree.map((pillar) => (
        <InitiativeTreeGroup
          key={pillar.id}
          label={`Pillar ${pillar.number}: ${pillar.name}`}
          depth={0}
        >
          {/* The scorecard sits directly under the pillar, above the objectives, because it rolls
              up across both of them. Filing it under either one would misrepresent it. */}
          <InitiativeLink
            href="/initiatives/scorecard"
            label="Scorecard"
            depth={1}
            pathname={pathname}
            emphasis
          />
          {pillar.objectives.map((objective) => (
            <InitiativeTreeGroup
              key={objective.id}
              label={`Objective ${objective.number}: ${objective.title}`}
              depth={1}
            >
              {objective.initiatives.map((initiative) => (
                <InitiativeTreeGroup
                  key={initiative.id}
                  label={`Initiative ${initiative.number}: ${initiative.name}`}
                  depth={2}
                >
                  {initiative.kpis.map((kpi) => (
                    <InitiativeKpiLink key={kpi.slug} kpi={kpi} depth={3} pathname={pathname} />
                  ))}
                </InitiativeTreeGroup>
              ))}
            </InitiativeTreeGroup>
          ))}
        </InitiativeTreeGroup>
      ))}
    </>
  );
}

export default function AdminMenu({ opened, toggleDrawer, children }) {
  const location = useLocation();
  const { instance, accounts } = useMsal();
  const [username, setUsername] = useState("");

  // Mantine color scheme hooks
  const { setColorScheme } = useMantineColorScheme();
  const computedColorScheme = useComputedColorScheme('light', { getInitialValueInEffect: false });

  // State for collapsible sections
  const [initiativesOpen, setInitiativesOpen] = useState(true);
  const [salesOpen, setSalesOpen] = useState(true);
  const [funnelOpen, setFunnelOpen] = useState(true);
  const [purchasingOpen, setPurchasingOpen] = useState(true);
  const [customersOpen, setCustomersOpen] = useState(true);
  const [repairsOpen, setRepairsOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(true);
  const [pentagonOpen, setPentagonOpen] = useState(true);

  const [toolsOpen, setToolsOpen] = useState(false);
  const [classificationsOpen, setClassificationsOpen] = useState(false);
  const [learningOpen, setLearningOpen] = useState(false);

  useEffect(() => {
    if (accounts.length > 0) {
      const name = accounts[0].name || accounts[0].username || "User";
      setUsername(name);
    }
  }, [accounts]);

  const { data: roles = [], isLoading, error } = useAuthenticatedData(`${config.apiBaseUrl}/my/roles`);

  const handleLogout = async () => {
    const logoutRequest = {
      postLogoutRedirectUri: window.location.origin,
    };

    try {
      await instance.logoutRedirect(logoutRequest);
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Theme toggle function
  const toggleColorScheme = () => {
    setColorScheme(computedColorScheme === 'dark' ? 'light' : 'dark');
  };

  const NestedMenuItem = ({ href, icon, text, requiredRoles = [] }) => {
    const isVisible = requiredRoles.length === 0 || (Array.isArray(roles) && roles.some(role => requiredRoles.includes(role)));

    if (!isVisible) return null;

    let isActive = location.pathname === href;

    // Custom active state logic
    if (text === "Overview" && location.pathname === '/initiatives') {
      isActive = true;
    }
    if (text === "Customers" && location.pathname.startsWith('/customer')) {
      isActive = true;
    }
    if (text === "Sales" && location.pathname.startsWith('/sales')) {
      isActive = true;
    }
    if (text === "Classifications" && location.pathname.startsWith('/part-classifications')) {
      isActive = true;
    }
    if (text === "Parts Evaluator" && location.pathname.startsWith('/parts-eval')) {
      isActive = true;
    }
    if (text === "Manager's Scorecard" && href.includes('sales/manager') && location.pathname.startsWith('/sales/manager/scorecard')) {
      isActive = true;
    }
    if (text === "Adjustments" && (location.pathname.startsWith('/sales/manager/invoices') || location.pathname.startsWith('/sales/manager/overrides') || location.pathname === '/sales/manager')) {
      isActive = true;
    }
    if (text === "Commissions" && location.pathname.startsWith('/sales/manager/commissions')) {
      isActive = true;
    }
    if (text === "Manager's Scorecard" && href.includes('purchasing/manager') && location.pathname.startsWith('/supply-chain/purchasing/manager')) {
      isActive = true;
    }

    return (
      <NavLink
        component={RouterLink}
        to={href}
        label={text}
        leftSection={React.cloneElement(icon, { size: 16 })}
        active={isActive}
        styles={(theme) => ({
          root: {
            paddingLeft: theme.spacing.xl,
            fontSize: theme.fontSizes.sm,
          },
          label: {
            fontWeight: isActive ? 600 : 400,
          }
        })}
      />
    );
  };

  const MenuItem = ({ href, icon, text, requiredRoles = [] }) => {
    const isVisible = requiredRoles.length === 0 || (Array.isArray(roles) && roles.some(role => requiredRoles.includes(role)));

    if (!isVisible) return null;

    let isActive = location.pathname === href;
    if (text === "Data Sets" && location.pathname.startsWith('/datasets')) {
      isActive = true;
    }

    return (
      <NavLink
        component={RouterLink}
        to={href}
        label={text}
        leftSection={React.cloneElement(icon, { size: 18 })}
        active={isActive}
        styles={(theme) => ({
          label: {
            fontWeight: isActive ? 600 : 400,
          }
        })}
      />
    );
  };

  // Sections take requiredRoles for the same reason items do — gating only the children would leave an
  // empty heading that expands into nothing. Gating the section hides the header with it.
  const CollapsibleSection = ({ title, icon, isOpen, onToggle, children, requiredRoles = [] }) => {
    const isVisible = requiredRoles.length === 0 || (Array.isArray(roles) && roles.some(role => requiredRoles.includes(role)));

    if (!isVisible) return null;

    return (
      <div>
        <UnstyledButton
          onClick={onToggle}
          w="100%"
          p="sm"
        >
          <Group justify="space-between" w="100%">
            <Group>
              {React.cloneElement(icon, { size: 18 })}
              <Text fw={600}>{title}</Text>
            </Group>
            {isOpen ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
          </Group>
        </UnstyledButton>
        <Collapse in={isOpen}>
          <div style={{ paddingLeft: '8px' }}>
            {children}
          </div>
        </Collapse>
      </div>
    );
  };

  if (isLoading) return <div>Loading...</div>;

  return (
    <AppShell
      header={{ height: 60 }}
      navbar={{
        width: DRAWER_WIDTH,
        breakpoint: 'sm',
        collapsed: { mobile: !opened, desktop: !opened }
      }}
      padding="md"
    >
      <AppShell.Header>
        <Group justify="space-between" h="100%" px="md">
          <Group>
            <Burger
              opened={opened}
              onClick={toggleDrawer}
              size="sm"
            />
            <Image src="/nexus_logo_blue.png" alt="Nexus Logo" h={30} />
          </Group>

          <Group>
            <Group gap={4}>
              <Text size="sm">Logged in as:</Text>
              <Text size="sm" fw={600}>{username}</Text>
              <Anchor
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  handleLogout();
                }}
                style={{ cursor: 'pointer' }}
              >
                [log out]
              </Anchor>
            </Group>

            {/* Theme Toggle Button */}
            <ActionIcon
              onClick={toggleColorScheme}
              variant="default"
              size="lg"
              aria-label="Toggle color scheme"
            >
              {computedColorScheme === 'dark' ? (
                <IconSun size={18} />
              ) : (
                <IconMoon size={18} />
              )}
            </ActionIcon>

            <Image src="/its_logo3.png" alt="ITS Logo" h={40} />
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="md">
        <ScrollArea>
          <Stack gap="xs">
            {/* Initiatives Section — 2026 objectives & KPIs. Experimental (alpha/beta channel)
                only, header and all. Definitions are still in flux; the exec team reviews here. */}
            <CollapsibleSection
              title="Initiatives"
              icon={<IconTargetArrow />}
              isOpen={initiativesOpen}
              onToggle={() => setInitiativesOpen(!initiativesOpen)}
              requiredRoles={["Experimental"]}
            >
              {/* Rendered directly rather than via NestedMenuItem so it sits at the tree's
                  top-level indent instead of the deeper nested-item one. */}
              <NavLink
                component={RouterLink}
                to="/initiatives"
                label="Overview"
                leftSection={<IconFlag size={16} />}
                active={location.pathname === '/initiatives'}
                styles={(theme) => ({
                  root: { paddingLeft: TREE_INDENT(0), paddingTop: 4, paddingBottom: 4 },
                  label: {
                    fontSize: theme.fontSizes.xs,
                    fontWeight: location.pathname === '/initiatives' ? 600 : 400,
                  },
                })}
              />
              <InitiativeTree tree={initiativeTree} pathname={location.pathname} />
            </CollapsibleSection>

            {/* Sales Section */}
            <CollapsibleSection
              title="Sales"
              icon={<IconCurrencyDollar />}
              isOpen={salesOpen}
              onToggle={() => setSalesOpen(!salesOpen)}
            >
           {/*    <NestedMenuItem href="/sales-accelerator" icon={<IconUsers />} text="Sales Dashboard" />*/}
              <NestedMenuItem href="/sales/opportunities" icon={<IconFlag />} text="Opportunities" requiredRoles={["Experimental"]} />
              <NestedMenuItem href="/sales/cockpit" icon={<IconManualGearbox />} text="Quoting Cockpit" requiredRoles={["Experimental"]} />
              <NestedMenuItem href="/sales/department/scorecard" icon={<IconUsers />} text="Department Scorecard" />
              <NestedMenuItem href="/sales/manager/scorecard" icon={<IconManualGearbox />} text="Manager's Scorecard" />
              <NestedMenuItem href="/sales/salesperson" icon={<IconUser />} text="Individual Scorecard" />
              {/* <NestedMenuItem href="/sales/overrides" icon={<IconAdjustments />} text="Adjustments" /> */}
              <NestedMenuItem href="/sales/manager/commissions" icon={<IconReportMoney />} text="Commissions" requiredRoles={["Experimental"]} />
              { <NestedMenuItem href="/erp/rotabull-pricing" icon={<IconTag />} text="Rotabull Pricing" /> }
              { <NestedMenuItem href="/erp/rfq-quote-traffic" icon={<IconReportMoney />} text="Rotabull Traffic" /> }
              {/* <NestedMenuItem href="/erp/rotabull-simulation" icon={<IconChartBar />} text="Rotabull Simulation" /> */}
              { <NestedMenuItem href="/erp/quotes-analytics" icon={<IconChartLine />} text="Quote Analytics" /> }
            </CollapsibleSection>

            {/* Funnel Section — Experimental (alpha/beta channel) only, header and all. */}
            <CollapsibleSection
              title="Funnel"
              icon={<IconFilter />}
              isOpen={funnelOpen}
              onToggle={() => setFunnelOpen(!funnelOpen)}
              requiredRoles={["Experimental"]}
            >
              <NestedMenuItem href="/sales/funnel/inbound-emails" icon={<IconMail />} text="Inbound Emails" />
              <NestedMenuItem href="/sales/funnel/rotabull-deals" icon={<IconChartBar />} text="Rotabull Deals" />
              <NestedMenuItem href="/sales/funnel/manager-overview" icon={<IconUsers />} text="Manager Overview" />
              <NestedMenuItem href="/sales/funnel/my-deals" icon={<IconUser />} text="My Deals" />
              <NestedMenuItem href="/sales/funnel/guidance" icon={<IconScale />} text="Guidance" />
              {/* TODO restrict to admin/mgmt before go-live — open to all for now (fast iteration) */}
              <NestedMenuItem href="/sales/funnel/teaching" icon={<IconScale />} text="Teaching" />
              <NestedMenuItem href="/sales/funnel/nymeria-settings" icon={<IconScale />} text="AI Sales Agent settings" />
              <NestedMenuItem href="/sales/funnel/documentation" icon={<IconBook />} text="Documentation" />
              <NestedMenuItem href="/ai/traffic" icon={<IconReportMoney />} text="AI Traffic" />
              <NestedMenuItem href="/sales/funnel/rotabull-companies" icon={<IconSearch />} text="Rotabull Companies" />
            </CollapsibleSection>

            {/* Customers Section */}
            <CollapsibleSection
              title="Customers"
              icon={<IconPlaneTilt />}
              isOpen={customersOpen}
              onToggle={() => setCustomersOpen(!customersOpen)}
            >
              <NestedMenuItem href="/customer" icon={<IconUser />} text="Customer Scorecard" />
              <NestedMenuItem href="/data/fleet-explorer" icon={<IconUser />} text="Fleet Intelligence" />
            {/*    <NestedMenuItem href="/sales/inventory-matcher" icon={<IconUser />} text="Inventory Matcher" />*/}

            </CollapsibleSection>

            {/* Purchasing Section */}
            <CollapsibleSection
              title="Purchasing"
              icon={<IconTruck />}
              isOpen={purchasingOpen}
              onToggle={() => setPurchasingOpen(!purchasingOpen)}
            >
              <NestedMenuItem href="/supply-chain/purchasing/department/scorecard" icon={<IconUsers />} text="Department Scorecard" />
              <NestedMenuItem href="/supply-chain/purchasing/manager/scorecard" icon={<IconManualGearbox />} text="Manager's Scorecard" />
              <NestedMenuItem href="/supply-chain/purchasing/scorecard" icon={<IconUser />} text="Individual Scorecard" />
            </CollapsibleSection>

            {/* Repairs Section */}
            <CollapsibleSection
              title="Repairs"
              icon={<IconTruck />}
              isOpen={repairsOpen}
              onToggle={() => setRepairsOpen(!repairsOpen)}
            >
              <NestedMenuItem href="/supply-chain/repairs/department/scorecard" icon={<IconUsers />} text="Department Scorecard" />
              <NestedMenuItem href="/supply-chain/repairs/manager/scorecard" icon={<IconManualGearbox />} text="Manager's Scorecard" />
              <NestedMenuItem href="/supply-chain/repairs/scorecard" icon={<IconUser />} text="Individual Scorecard" />
            </CollapsibleSection>

            {/* Admin Section */}
            <CollapsibleSection
              title="Admin"
              icon={<IconShieldCheck />}
              isOpen={adminOpen}
              onToggle={() => setAdminOpen(!adminOpen)}
            >
              <NestedMenuItem href="/admin/manage-groups" icon={<IconUsers />} text="Manage Groups" requiredRoles={["admin"]} />
              <NestedMenuItem href="/admin/services" icon={<IconServer />} text="Service Control" requiredRoles={["admin"]} />
           {/*   <NestedMenuItem href="/admin/manage-ata" icon={<IconPlane />} text="Manage ATA" />
              <NestedMenuItem href="/admin/manage-goals" icon={<IconFlag />} text="Manage Goals" /> 
              */}
            </CollapsibleSection>

            {/* Admin Section
            <CollapsibleSection
              title="Pentagon"
              icon={<IconPentagon />}
              isOpen={pentagonOpen}
              onToggle={() => setPentagonOpen(!pentagonOpen)}
            >
              <NestedMenuItem href="/erp/quotes" icon={<IconReportMoney />} text="Quotes" />

            </CollapsibleSection>
            

            {/* Single menu items  
            <MenuItem href="/frontdesk" icon={<IconFilter />} text="Sales Funnel" />
 */}
            {/* Parts Section */}
            <CollapsibleSection
              title="Parts"
              icon={<IconPackage />}
              isOpen={classificationsOpen}
              onToggle={() => setClassificationsOpen(!classificationsOpen)}
            >
              <NestedMenuItem href="/part-classifications" icon={<IconAbc />} text="Classifications" />
            </CollapsibleSection>

            {/* Tools Section 
            <CollapsibleSection
              title="Tools"
              icon={<IconTools />}
              isOpen={toolsOpen}
              onToggle={() => setToolsOpen(!toolsOpen)}
            >
              <NestedMenuItem href="/parts-evaluation" icon={<IconScale />} text="Parts Evaluator" />
            </CollapsibleSection>*/}

            {/* Learning Section 
            <CollapsibleSection
              title="Learning"
              icon={<IconSchool />}
              isOpen={learningOpen}
              onToggle={() => setLearningOpen(!learningOpen)}
            >
              <NestedMenuItem href="/learn/modules" icon={<IconVideo />} text="Training Modules" requiredRoles={["learning"]} />
            </CollapsibleSection>*/}

            {/* More single menu items 
            <MenuItem href="/datasets" icon={<IconFlask />} text="Data Sets" requiredRoles={["experiments"]} />
            <MenuItem href="/reports" icon={<IconChartLine />} text="Reports" requiredRoles={["reports"]} />
            <MenuItem href="/dwexplorer" icon={<IconSearch />} text="Data Warehouse Explorer" requiredRoles={["dw"]} />
              <MenuItem href="/repair-dashboard" icon={<IconAtom />} text="Repair Dashboard" requiredRoles={["repair_wizard"]} />
            
            */}
            <MenuItem href="/query-commander" icon={<IconAtom />} text="Query Commander" requiredRoles={["queryCommander"]} />
            <MenuItem href="/query-commander/health" icon={<IconShieldCheck />} text="Query Health" requiredRoles={["queryCommander"]} />
            <MenuItem href="/admin/scorecards" icon={<IconShieldCheck />} text="Scorecard Admin" requiredRoles={["queryCommander"]} />
            <MenuItem href="/bug-report" icon={<IconBug />} text="Report Bug" requiredRoles={["report_bug"]} />
           {/*   <MenuItem href="/about" icon={<IconInfoCircle />} text="About" requiredRoles={["about"]} /> */}
          
          </Stack>
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        {children}
      </AppShell.Main>
    </AppShell>
  );
}